﻿import { Kb3dModel } from '../decorators/kb3d-decorator';
import { IUvData } from '../kb3d-models';
import { MeshNode } from '../meshes/mesh-node';
import { Token } from '../token';

export const NEW_GEOMETRY = 'NEW_GEOMETRY';

/** IUvData has number[] props needed for server, but we want
 * them to be float32array here
 */
export type UvBuffer = {
    [P in keyof IUvData]: Float32Array;
};

export interface KBVertexData extends UvBuffer {
    indices?: Uint32Array;
    positions?: Float32Array;
    normals?: Float32Array;
    tangents?: Float32Array;
}

const MAX_INT16 = 32767;
const MAX_UINT16 = 65535;

@Kb3dModel({
    token: Token.KbGeometry,
    trackChanges: false,
})
export class KbGeometry implements KBVertexData {
    indices?: Uint32Array;
    positions?: Float32Array;
    normals?: Float32Array;
    tangents?: Float32Array;
    uvs?: Float32Array;
    colors?: Float32Array;
    /**
     * This property is used by the mesh renderer when a node has a pretransform (baked transform) defined.
     * The first time the geometry is transformed, it's mutated. The 4x4 transform matrix is stored here.
     * When this geometry is used in the future, this transform has to be taken into account for any future transforms (or lack of a transform)
     * This is so that we can reuse a transformed geometry if many nodes have the same transform or if a single node needs to rebuild
     * it's own geometry often, which is the most common use case.
     */
    _hasTransform?: number[];

    shared = false;

    /**
     * A geometry object. Geometries are immutable, once they are created they should not be modified.
     */
    constructor() {}

    clone() {
        const newGeometry = new KbGeometry();
        newGeometry.indices = this.indices;
        newGeometry.positions = this.positions;
        newGeometry.normals = this.normals;
        newGeometry.tangents = this.tangents;
        newGeometry.uvs = this.uvs;

        return newGeometry;
    }

    /**
     * Freeze a geometry to enforce the immutability of geometries
     */
    // freeze() {
    //     Object.freeze(this);
    //     Object.freeze(this.vertexData);
    // }

    // update(vertexData: Partial<KBVertexData & IUvData>) {
    //     const newVertexData = { ...this.vertexData, ...vertexData };
    //     const newGeometry = new KbGeometry(newVertexData, this.id);
    //     return newGeometry;
    // }

    extractFromBinary(fileData: ArrayBuffer, meshNode: MeshNode): void {
        const dataView = new DataView(fileData);
        let curPos = 0;

        // --- Header extraction ---
        const headerChars = [];
        for (let i = 0; i < 4; i++) {
            headerChars.push(String.fromCharCode(dataView.getUint8(curPos++)));
        }
        const headerId = headerChars.join('').substring(0, 4); // "KB3D"
        const headerVersion = dataView.getUint8(4);

        // --- Indices ---
        if (headerId.toLowerCase() !== 'kb3d') {
            curPos = 0;
        } else {
            curPos = 8;
        }
        const indicesOffset = dataView.getInt32(curPos, true);
        curPos += 4;
        const indicesDataSize = dataView.getInt32(curPos, true);
        curPos += 4;

        let indices: Uint32Array;
        if (indicesDataSize === 16) {
            const indices16 = new Uint16Array(fileData, curPos, indicesOffset / 2);
            indices = Uint32Array.from(indices16);
            curPos += indicesOffset;
        } else if (indicesDataSize === 32) {
            indices = new Uint32Array(fileData, curPos, indicesOffset / 4);
            curPos += indicesOffset;
        } else {
            throw Error('Unknown indices data size ' + meshNode.name);
        }

        // --- Positions ---
        const posOffset = dataView.getInt32(curPos, true);
        curPos += 4;
        const posDataSize = dataView.getInt32(curPos, true);
        curPos += 4;
        const posRatio = dataView.getInt32(curPos, true);
        curPos += 4;

        let positions: Float32Array;
        if (posDataSize === 32) {
            positions = new Float32Array(fileData.slice(curPos, curPos + posOffset));
            for (let i = 0; i < positions.length; i++) {
                positions[i] /= posRatio;
            }
            curPos += posOffset;
        } else {
            throw Error('Invalid positions in binary ' + meshNode.name);
        }

        // --- Normals ---
        const norOffset = dataView.getInt32(curPos, true);
        curPos += 4;
        const norDataSize = dataView.getInt32(curPos, true);
        curPos += 4;
        const norRatio = dataView.getInt32(curPos, true);
        curPos += 4;

        let normals: Float32Array;
        if (norDataSize === 16) {
            const normalsInt = new Int16Array(fileData.slice(curPos, curPos + norOffset));
            normals = new Float32Array(normalsInt.length);
            for (let i = 0; i < normalsInt.length; i++) {
                normals[i] = normalsInt[i] / norRatio;
            }
            curPos += norOffset;
        } else {
            throw Error('Invalid normals in binary ' + meshNode.name);
        }

        // --- UVs ---
        const uvsOffset = dataView.getInt32(curPos, true);
        curPos += 4;
        const uvsDataSize = dataView.getInt32(curPos, true);
        curPos += 4;
        const uvsRatio = dataView.getInt32(curPos, true);
        curPos += 4;

        let uvs: Float32Array;
        if (uvsDataSize === 16) {
            const uvsInt = new Int16Array(fileData.slice(curPos, curPos + uvsOffset));
            uvs = new Float32Array(uvsInt.length);
            for (let i = 0; i < uvsInt.length; i++) {
                uvs[i] = uvsInt[i] / uvsRatio;
            }
            curPos += uvsOffset;
        } else {
            throw Error('Invalid uvs in binary ' + meshNode.name);
        }

        // --- Colors (only if header >= KB3D2) ---
        let colors = new Float32Array();
        if (headerId.toLowerCase() === 'kb3d' && headerVersion >= 2) {
            const colOffset = dataView.getInt32(curPos, true);
            curPos += 4;
            const colDataSize = dataView.getInt32(curPos, true);
            curPos += 4;
            const colRatio = dataView.getInt32(curPos, true);
            curPos += 4;

            if (colDataSize === 16) {
                const colorsInt = new Int16Array(fileData.slice(curPos, curPos + colOffset));
                colors = new Float32Array(colorsInt.length);
                for (let i = 0; i < colorsInt.length; i++) {
                    colors[i] = colorsInt[i] / colRatio;
                }
                curPos += colOffset;
            } else {
                throw Error('Invalid colors in binary ' + meshNode.name);
            }
        }

        // Error reporting
        if (indices.length % 3 > 0) {
            console.warn('Incomplete indices triangles in mesh ' + meshNode.name);
        }
        if (positions.length % 3 > 0) {
            console.warn('Incomplete position vectors in mesh ' + meshNode.name);
        }
        if (normals.length % 3 > 0) {
            console.warn('Incomplete normal vectors in mesh ' + meshNode.name);
        }
        if (uvs.length % 2 > 0) {
            console.warn('Incomplete uv vectors in mesh ' + meshNode.name);
        }
        if (colors.length && colors.length % 4 > 0) {
            console.warn('Incomplete color vectors in mesh ' + meshNode.name);
        }
        if (positions.length !== normals.length) {
            console.warn('Normals and positions array size mismatch in ' + meshNode.name);
        }
        if (uvs.length > 0 && positions.length / 3 !== uvs.length / 2) {
            console.warn('UV array size mismatch in ' + meshNode.name);
        }
        if (colors.length && positions.length / 3 !== colors.length / 4) {
            console.warn('Color array size mismatch in ' + meshNode.name);
        }

        this.positions = positions;
        this.indices = indices;
        if (normals.length > 0) this.normals = normals;
        if (uvs.length > 0) this.uvs = uvs;
        if (colors.length > 0) this.colors = colors;
    }

    toBinary(): ArrayBuffer | undefined {
        if (!this.positions || !this.indices) {
            return;
        }
        const positions = this.positions;
        const indices = this.indices;
        const normals = this.normals || new Float32Array([]);
        const uvs = this.uvs || new Float32Array([]);
        const colors = this.colors || new Float32Array([]);

        let fileLen = 0;
        let indicesOffset = 16; // indices data markers (first 8 bytes reserved for header)
        let indicesBits = 0;
        let indicesLength = 0;
        let posOffset = 0;
        let posBits = 0;
        let posLength = 0;
        let normalsOffset = 0;
        let normalsBits = 0;
        let normalsLength = 0;
        let uvsOffset = 0;
        let uvsBits = 0;
        let uvsLength = 0;
        let colorsOffset = 0;
        let colorsBits = 0;
        let colorsLength = 0;

        fileLen += indicesOffset;
        if (canFitInUInt16Bit(indices)) {
            indicesBits = 16;
            indicesLength = indices.length * 2;
            fileLen += indicesLength;
        } else {
            indicesBits = 32;
            indicesLength = indices.length * 4;
            fileLen += indicesLength;
        }

        fileLen += 12; // position data markers
        posOffset = fileLen;
        posBits = 32;
        posLength = positions.length * 4;
        fileLen += posLength;

        fileLen += 12; // normals data markers
        normalsOffset = fileLen;
        normalsBits = 16;
        normalsLength = normals.length * 2;
        fileLen += normalsLength;

        fileLen += 12; // uv data markers
        uvsOffset = fileLen;
        uvsBits = 16;
        uvsLength = uvs.length * 2;
        fileLen += uvsLength;

        fileLen += 12; // color data markers
        colorsOffset = fileLen;
        colorsBits = 16;
        colorsLength = colors.length * 2;
        fileLen += colorsLength;

        const buffer = new ArrayBuffer(fileLen);
        const bufferView = new DataView(buffer);

        // --- Added: Write header "KB3D2" into the first 8 bytes ---
        const header = 'KB3D';
        let i = 0;
        for (; i < header.length; i++) {
            bufferView.setUint8(i, header.charCodeAt(i));
        }
        bufferView.setUint8(4, 2); // version 2
        i += 1;
        for (; i < 8; i++) {
            bufferView.setUint8(i, 0); // pad the rest
        }
        // -----------------------------------------------------------

        // Indices
        bufferView.setInt32(indicesOffset - 8, indicesLength, true); // NOTICE that by default JS is big endian, but since our servers are x86 we are little endian
        bufferView.setInt32(indicesOffset - 4, indicesBits, true);
        if (indicesBits == 16) {
            for (let i = 0; i < indices.length; i++) {
                bufferView.setUint16(indicesOffset, indices[i], true);
                indicesOffset += 2;
            }
        } else if (indicesBits == 32) {
            for (let i = 0; i < indices.length; i++) {
                bufferView.setUint32(indicesOffset, indices[i], true);
                indicesOffset += 4;
            }
        }

        // Positions
        bufferView.setInt32(posOffset - 12, posLength, true);
        bufferView.setInt32(posOffset - 8, posBits, true);
        bufferView.setInt32(posOffset - 4, 1, true); // ratio
        if (posBits == 32) {
            for (let i = 0; i < positions.length; i++) {
                bufferView.setFloat32(posOffset, positions[i], true);
                posOffset += 4;
            }
        } else {
            console.error('Fix me - non-32bit positions not supported');
        }

        // Normals
        bufferView.setInt32(normalsOffset - 12, normalsLength, true);
        bufferView.setInt32(normalsOffset - 8, normalsBits, true);
        bufferView.setInt32(normalsOffset - 4, MAX_INT16, true);
        if (normalsBits == 16) {
            for (let i = 0; i < normals.length; i++) {
                const n = floatToIntStore(normals[i], MAX_INT16);
                bufferView.setInt16(normalsOffset, n, true);
                normalsOffset += 2;
            }
        } else {
            console.error('Fix me - non-16bit normals not supported');
        }

        // UVs
        bufferView.setInt32(uvsOffset - 12, uvsLength, true);
        bufferView.setInt32(uvsOffset - 8, uvsBits, true);
        bufferView.setInt32(uvsOffset - 4, MAX_INT16, true);
        if (uvsBits == 16) {
            for (let i = 0; i < uvs.length; i++) {
                const n = floatToIntStore(uvs[i], MAX_INT16);
                bufferView.setInt16(uvsOffset, n, true);
                uvsOffset += 2;
            }
        } else {
            console.error('Fix me - non-16bit uvs not supported');
        }

        // Colors
        bufferView.setInt32(colorsOffset - 12, colorsLength, true);
        bufferView.setInt32(colorsOffset - 8, colorsBits, true);
        bufferView.setInt32(colorsOffset - 4, MAX_INT16, true);
        if (colorsBits == 16) {
            for (let i = 0; i < colors.length; i++) {
                const n = floatToIntStore(colors[i], MAX_INT16);
                bufferView.setInt16(colorsOffset, n, true);
                colorsOffset += 2;
            }
        } else {
            console.error('Fix me - non-16bit colors not supported');
        }

        return buffer;
    }
}

// function canFitInInt16Bit(inArray: Float32Array): boolean {

//     for (let i = 0; i < inArray.length; i++) {
//         const val = inArray[i];
//         if (val > 327.68 || val < -327.68) {
//             return false;
//         }
//     }
//     return true;
// }

// function canFitInInt8Bit(inArray: Float32Array): boolean {
//     for (let i = 0; i < inArray.length; i++) {
//         const val = inArray[i];
//         if (val > 1.28 || val < -1.28) {
//             return false;
//         }
//     }
//     return true;
// }

function canFitInUInt16Bit(inArray: Int32Array | Uint32Array) {
    for (let i = 0; i < inArray.length; i++) {
        const val = inArray[i];
        if (val > MAX_UINT16) {
            return false;
        }
    }
    return true;
}

function floatToIntStore(float: number, maxInt: number, maxVal = 1): number {
    // this function convert a value between -327.68 and 327.68 to an int 16. we use the convention to have only 2 decimal numbers
    float = (Math.min(Math.max(-maxVal, float), maxVal) * maxInt) / maxVal;

    const outVal = Math.trunc(float);
    return outVal;
}
